import Vue from "vue";
import axios from "@/axios";
import tinycolor from "tinycolor2";
import { setFavIcon } from "@shared/utils/functions";
import { parseThemeData, getColorValues, colorShades } from "./utils";
import { focusColors } from "./utils/constants";
import { cloneDeep } from "lodash";

// TODO Now, all theme color variables described here can be removed since they are calculated dynamically now
// TODO Before removal, such an approach must be applied for all relevant applications!

export default {
  state: {
    // settings from the server
    tenant_config: {},
    client_config: {},
    // current frontend settings:
    theme: {
      defaultColors: {
        main_color: {
          // primary
          var: "--theme-color-main",
          value: "#0D69D5",
        },
        table_header_footer: {
          var: "--theme-color-thead",
          value: "#029ADB",
        },
        section_selection: {
          var: "--theme-color-selection",
          value: "#029ADB",
        },
        page_background: {
          var: "--theme-color-page-bg",
          value: "#EDF2F7",
        },
        text_menu_icon: {
          var: "--theme-color-text",
          value: "#282828",
        },
        line_sepration: {
          var: "--theme-color-line",
          value: "#DDDDDD",
        },
        box_stroke_color: {
          var: "--theme-color-box-stroke",
          value: "#DDDDDD",
        },
        white_text: {
          var: "--theme-color-white-text",
          value: "#FFFFFF",
        },
        card_bg: {
          var: "--theme-card-background",
          value: "#ffffff",
        },
      },
      // temporary frontend colors:
      colors: {},
      colorScheme: {
        main_color: {
          // primary
          name: "Main Color",
          var: "--theme-color-main",
          daisyui: ["primary", "accent"],
          shades: "primary",
        },
        accent: {
          var: "--a",
        },
        "accent-focus": {
          var: "--af",
        },
        primary: {
          var: "--p",
        },
        "primary-focus": {
          var: "--pf",
        },
        "primary-50": {
          var: "--theme-color-primary-50",
        },
        "primary-100": {
          var: "--theme-color-primary-100",
        },
        "primary-200": {
          var: "--theme-color-primary-200",
        },
        "primary-300": {
          var: "--theme-color-primary-300",
        },
        "primary-500": {
          var: "--theme-color-primary-500",
        },
        "primary-600": {
          var: "--theme-color-primary-600",
        },
        opacity: {
          var: "--tw-bg-opacity",
        },
        table_header_footer: {
          name: "Table Header/Footer",
          var: "--theme-color-thead",
        },
        section_selection: {
          name: "Section Selection",
          var: "--theme-color-selection",
        },
        page_background: {
          name: "Page Background",
          var: "--theme-color-page-bg",
        },
        text_menu_icon: {
          name: "Text & Menu Icon",
          var: "--theme-color-text",
          daisyui: ["base-content"],
          shades: "base-content",
        },
        "base-content": {
          var: "--bc",
        },
        "base-content-100": {
          var: "--theme-color-text-100",
        },
        "base-content-200": {
          var: "--theme-color-text-200",
        },
        "base-content-300": {
          var: "--theme-color-text-300",
        },
        "base-content-600": {
          var: "--theme-color-text-600",
        },
        "base-content-800": {
          var: "--theme-color-text-800",
        },
        line_sepration: {
          name: "Line Separation",
          var: "--theme-color-line",
        },
        box_stroke_color: {
          name: "Box Stroke Color",
          var: "--theme-color-box-stroke",
        },
        white_text: {
          name: "White Text",
          var: "--theme-color-white-text",
          daisyui: [
            "primary-content",
            "secondary-content",
            "accent-content",
            "neutral-content",
          ],
          shades: "white_text",
        },
        "white_text-300": {
          var: "--theme-color-white-text-300",
        },
        "white_text-600": {
          var: "--theme-color-white-text-600",
        },
        "primary-content": {
          var: "--pc",
        },
        "secondary-content": {
          var: "--sc",
        },
        "accent-content": {
          var: "--ac",
        },
        "neutral-content": {
          var: "--nc",
        },
        card_bg: {
          var: "--theme-card-background",
          name: "Card Background",
        },
      },
      logo: "",
      favicon: "",
      clientLogo: "",
      clientFavIcon: "",
      use_default: false,
    },
  },

  getters: {
    getTenantConfig: (state) => state.tenant_config,
    getClientConfig: (state) => state.client_config,
    getThemeColors: (state) => state.theme.colors,
    getDefaultColors: (state) => state.theme.defaultColors,
    getDefaultUIConfig: (state) => ({
      color_config: getColorValues(state.theme.defaultColors),
    }),
    getThemeLogo: (state) => state.theme.logo,
    getThemeFavicon: (state) => state.theme.favicon,
    getThemeClientLogo: (state) => state.theme.clientLogo,
    getThemeClientFavicon: (state) => state.theme.clientFavIcon,
    getThemeUseDefault: (state) => state.theme.use_default, // && !Object.keys(state.theme.colors).length
    getIsDirtyColorSchema: (state) => JSON.stringify(state.theme.colors) !== JSON.stringify(state.theme.defaultColors), // Introduced to prevent the application of default colors when the user switches the 'Use Default Settings' checkbox without saving.
    // eslint-disable-next-line no-unused-vars
    appThemeColors(state, getters) { 
      const colors = (getters.getThemeUseDefault === false || getters.getIsDirtyColorSchema) ? getters.getThemeColors : getters.getDefaultColors;
      return Object.values(colors).reduce((result, color) => {
        if (color.value) result[color.var] = color.value;
        return result;
      }, {})
    }
  },

  mutations: {
    SET_DEFAULT_COLOR_VALUES(state, values) {
      Object.entries(values).forEach(([key, value]) => {
        if (state.theme.defaultColors[key]) {
          Vue.set(state.theme.defaultColors[key], "value", value);
        }
      });
    },
    SET_TENANT_CONFIG(state, payload) {
      state.theme.logo = payload.logo_url;
      state.theme.favicon = payload?.favicon_url;
      // Vue.set(state.theme, "logo", payload.logo_url);
      // Vue.set(state.theme, "favicon", payload?.favicon_url);
      Vue.set(state, "tenant_config", payload);
      Vue.set(state.theme, "use_default", payload.reset_flag);
      
      // parse colors from server:
      const serverColors = state.tenant_config?.uiconfig?.color_config;

      this.commit("SET_COLORS_WITH_SHADES", {
        colors: getColorValues(serverColors || state.theme.defaultColors),
      });
    },
    SET_TENANT_COLORS(state, colors) {
      const storedColors = state.tenant_config?.uiconfig;
      if (storedColors?.color_config) {
        Vue.set(storedColors, "color_config", getColorValues(colors));
      }
    },
    SET_CLIENT_CONFIG(state, payload) {
      Vue.set(state.theme, "clientLogo", payload.logo_url);
      Vue.set(state.theme, "clientFavIcon", payload?.favicon_url);

      Vue.set(state, "client_config", payload);
      Vue.set(state.theme, "use_default", payload.reset_flag);

      // parse colors from server:
      const serverColors = state.client_config?.uiconfig?.color_config;

      this.commit("SET_COLORS_WITH_SHADES", {
        colors: getColorValues(serverColors || state.theme.defaultColors),
      });
    },
    SET_CLIENT_COLORS(state, colors) {
      const storedColors = state.client_config?.uiconfig;
      if (storedColors?.color_config) {
        Vue.set(storedColors, "color_config", getColorValues(colors));
      }
    },
    SET_COLORS_WITH_SHADES(state, { colors, targetColors = state.theme.colors }) {
      Object.entries(colors).forEach(([color, value]) => {
        this.commit("SET_THEME_COLOR", { color, value, targetColors });
      })
    },
    SET_THEME_COLOR(state, { color, value, targetColors: targetColors = state.theme.colors}) {
      const colorScheme = state.theme.colorScheme;
      if (!targetColors[color]) {
        Vue.set(targetColors, color, colorScheme[color]);
      }

      Vue.set(targetColors[color], "value", value);
      const dColors = colorScheme[color].daisyui;
      // daisyui variables resetting:
      if (dColors) {
        const { h, s, l } = tinycolor(value).toHsl();
        const daisyuiColor = `${h} ${Math.round(s * 100)}% ${Math.round(
          l * 100
        )}%`;
        const { h: hf, s: sf, l: lf } = tinycolor(value)
          .lighten(8)
          .toHsl();
        const daisyuiFocusColor = `${hf} ${Math.round(sf * 100)}% ${Math.round(
          lf * 100
        )}%`;

        dColors.forEach((dColor) => {
          if (!targetColors[dColor]) {
            Vue.set(targetColors, dColor, cloneDeep(colorScheme[dColor]));
          }
          Vue.set(targetColors[dColor], "value", daisyuiColor);
          // new focus color:
          if (focusColors.includes(dColor)) {
            const focusName = `${dColor}-focus`;
            if (!targetColors[focusName]) {
              Vue.set(targetColors, focusName, cloneDeep(colorScheme[focusName]));
            }
            Vue.set(targetColors[focusName], "value", daisyuiFocusColor);
          }
        });
      }
      const shades = colorScheme[color].shades;
      // color shades:
      if (shades) {
        colorShades.forEach((shade) => {
          const vName = `${shades}-${shade.postfix}`;
          if (colorScheme[vName]) {
            if (!targetColors[vName]) {
              Vue.set(targetColors, vName, cloneDeep(colorScheme[vName]));
            }
            Vue.set(targetColors[vName], "value", shade.make(value));
          }
        });
      }
    },
    SET_TENANT_THEME_LOGO(state, payload) {
      Vue.set(state.theme, "logo", payload);
      let themeObj = localStorage.getItem("themeObj");
      if (themeObj) {
        themeObj = JSON.parse(themeObj);
        themeObj.logo_url = payload;
        localStorage.setItem("themeObj", JSON.stringify(themeObj));
      }
    },
    SET_CLIENT_THEME_LOGO(state, payload) {
      Vue.set(state.theme, "clientLogo", payload);
    },
    SET_THEME_FAVICON_CLIENT(state, payload) {
      Vue.set(state.theme, "clientFavIcon", payload);
    },
    SET_THEME_FAVICON_TENANT(state, payload) {
       Vue.set(state.theme, "favicon", payload);
        let themeObj = localStorage.getItem("themeObj");
        if (themeObj) {
          themeObj = JSON.parse(themeObj);
          themeObj.favicon_url = payload;
          localStorage.setItem("themeObj", JSON.stringify(themeObj));
        }
    },
    SET_THEME_USE_DEFAULT(state, payload) {
      Vue.set(state.theme, "use_default", payload);
    },
  },

  actions: {
    fetchUIConf: async ({ commit, dispatch, getters, rootGetters }, id) => {
      if (!id) {
        return;
      }
      try {
        const defaultUIConfig = getters.getDefaultUIConfig;
        // get tenant theme data
        const { data } = await axios.get(`tenant/${id}`);
        // parse theme data if it is in JSON format
        const tenantUIConfig = parseThemeData(data.uiconfig);
        data.uiconfig = tenantUIConfig || defaultUIConfig;

        let isResetFlag = data.reset_flag;
        let clientData = null;

        // all data with color config
        const themeData = JSON.stringify(data);
        // only ui config
        const theme = JSON.stringify(data.uiconfig);

        localStorage.setItem("themeObj", themeData);
        localStorage.setItem("theme", theme);

        commit("SET_TENANT_CONFIG", data);

        if(rootGetters.getIsClient) {
          // * Client details include theme config and should already be fetched
          const clientDataDetails = rootGetters.getClientData?.details;

          // set tenant colors as default theme colors for the client user if reset_flag isn't true:
          if (!isResetFlag) {
            commit ("SET_DEFAULT_COLOR_VALUES", data.uiconfig?.color_config || {});
          }

          if (clientDataDetails) {
            const {
              logo_url,
              favicon_url,
              uiconfig,
            } = clientDataDetails;

            const clientUIConfig = parseThemeData(uiconfig);

            const clientPayload = {
              logo_url,
              favicon_url,
              reset_flag: isResetFlag,
              uiconfig: clientUIConfig || tenantUIConfig || defaultUIConfig,
            }
            commit("SET_CLIENT_CONFIG", clientPayload);
            dispatch("handleFaviconAndLogoClient")
           
            clientData = clientDataDetails;
          }
        } else {
          dispatch("handleFaviconAndLogoTenant")
        }

        // calculate shades for default colors:
        commit("SET_COLORS_WITH_SHADES", {
          colors: getColorValues(getters.getDefaultColors),
          targetColors: getters.getDefaultColors,
        });

        // Reset colors to match defaultColors to prevent dirty state /getIsDirtyColorSchema/ on mount
        if (isResetFlag) {
          commit("SET_COLORS_WITH_SHADES", {
            colors: getColorValues(getters.getDefaultColors),
          });
        }

        return {
          tenantData: data,
          clientData,
        };
      } catch (error) {
        console.log(">>>> error", error);
      }
    },

    setTenant: async (_, payload) => {
      try {
          const body = {
            uiconfig: {
              ...payload.uiconfig,
            },
            reset_flag: payload.reset_flag,
            logo_url: payload.logo_url,
            favicon_url: payload.favicon_url,
          };
          let themeObj = JSON.stringify(body.uiconfig);
          localStorage.setItem("theme", themeObj);
          const { data } = await axios.patch(`tenant/uiconfig`, body);

          // Enable if need to refetch data:
          // dispatch("fetchUIConf", rootGetters.getTenantId)
          return data;
      } catch (error) {
        console.log(">>>> error", error);
        throw error;
      }
    },

    setClient: async (_, payload) => {
      try {
          const body = {
            uiconfig: {
              ...payload.uiconfig,
            },
            reset_flag: payload.reset_flag,
            logo_url: payload.logo_url,
            favicon_url: payload.favicon_url,
            client_id: payload.client_id,
          };
          let themeObj = JSON.stringify(body.uiconfig);
          localStorage.setItem("theme", themeObj);
          const { data } = await axios.post(`client/uiconfig`, body);

          // Enable if need to refetch data and refetch /global-auth/init as well for the client user theme refresh:
          // dispatch("fetchUIConf", rootGetters.getTenantId)
          return data;
      } catch (error) {
        console.log(">>>> error", error);
        throw error;
      }
    },

    setClientThemeFavicon: async ({ commit, getters }, payload) => {
      commit("SET_THEME_FAVICON_CLIENT", payload.favicon);

      if(payload.updateType === 'remove') {
        const favIcon = getters.getClientLogo || getters.getThemeFavicon || getters.getThemeLogo || '';
        setFavIcon(favIcon);
      } else {
        setFavIcon(payload.favicon);
      }
    },

    setTenantThemeFavicon: async ({ commit, }, payload) => {
      commit("SET_THEME_FAVICON_TENANT", payload);
      setFavIcon(payload);
    },

    setClientThemeLogo: async ({ commit, getters, dispatch }, payload) => {
      commit("SET_CLIENT_THEME_LOGO", payload.logo);

      if(payload.updateType === 'remove') {
        if(!getters.clientLogo){
          if(getters.getThemeLogo) {
            await dispatch("setTenantThemeLogo", getters.getThemeLogo);
          } else {
              await dispatch("setTenantThemeLogo", "");
          }
          const faviconFallBack = getters.getClientFavicon || getters.getThemeFavicon || getters.getThemeLogo || '';
          setFavIcon(faviconFallBack);
        }
      } else {
         if(!getters.getClientFavicon){
          setFavIcon(payload.logo);
          } else {
          setFavIcon(getters.getClientFavicon);
          }
      }
      
      
      // dispatch("handleFaviconAndLogoClient");
    },

    setTenantThemeLogo: async ({ commit, dispatch, getters}, payload) => {
      commit("SET_TENANT_THEME_LOGO", payload);
      if(!getters.getIsClient) {
        dispatch("handleFaviconAndLogoTenant");
      }
    },
    handleFaviconAndLogoTenant: async ({ state, dispatch }) => {
      if(state.theme.favicon) {
        dispatch("setTenantThemeFavicon", state.theme.favicon);
      } else if(state.theme.logo){
        setFavIcon(state.theme.logo);
      } else {
        setFavIcon("");
      }
    },

    handleFaviconAndLogoClient: async ({ state, commit, getters }) => {
      const logo = state.theme.clientLogo || state.theme.logo;
      // const favicon = state.theme.clientFavIcon || state.theme.favicon;
      commit("SET_CLIENT_THEME_LOGO", logo);
      if(!state.theme.clientFavIcon) {
        const favIcon = getters.getClientLogo || getters.getThemeFavicon || getters.getThemeLogo || '';
        setFavIcon(favIcon)
      } else {
        commit("SET_THEME_FAVICON_CLIENT", state.theme.clientFavIcon);
        setFavIcon(state.theme.clientFavIcon)
      }
      

      // if(state.theme.clientFavicon) {
      //   dispatch("setClientThemeFavicon", state.theme.clientFavIcon);
      // } else if(state.theme.clientLogo){
      //   setFavIcon(state.theme.clientLogo);
      // } else {
      //   setFavIcon("");
      // }
    }
  },
};
